import React from 'react';
import { PP_SUBNAV, PP_SUBNAV_ICONS } from '../../patients/config';
import type { PatientProfileTab } from '../../patients/types';
import { ppEyebrowClass } from '../../patients/utils';
import { Icon } from '../Icon/Icon';

export interface PatientProfileSubNavProps {
  tab: PatientProfileTab;
  onTabChange: (tab: PatientProfileTab) => void;
  /**
   * A real href per tab (amendment 30 §2). When given, each item is an `<a>`:
   * middle-click opens a new tab, refresh/back keep the tab, and a plain click
   * still routes client-side via `onTabChange`.
   */
  hrefFor?: (tab: PatientProfileTab) => string;
}

export function PatientProfileSubNav({ tab, onTabChange, hrefFor }: PatientProfileSubNavProps) {
  return (
    <aside className="pp-scroll sticky top-0 z-[4] flex h-full w-[214px] shrink-0 flex-col overflow-y-auto border-r border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-[22px]">
      <div className={ppEyebrowClass} style={{ color: 'var(--ink-3)', padding: '0 10px 12px' }}>
        Patient
      </div>
      <div className="flex flex-col gap-[3px]">
        {PP_SUBNAV.map((k) => {
          const on = tab === k;
          const href = hrefFor?.(k);
          const shared = {
            className: 'relative flex w-full cursor-pointer items-center gap-3 rounded-[var(--r-md)] border-0 px-3.5 py-[11px] text-left font-[var(--font-body)] no-underline',
            style: { background: on ? 'var(--cream-1)' : 'transparent', boxShadow: on ? 'var(--sh-1)' : 'none' },
          };
          if (href !== undefined) {
            return (
              <a
                key={k}
                href={href}
                onClick={(e) => {
                  // Plain left click routes client-side; modified clicks (new tab) fall through.
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  onTabChange(k);
                }}
                {...shared}
              >
                {on ? <span className="absolute bottom-[9px] left-0 top-[9px] w-[3px] rounded-full bg-[var(--ai-3)]" aria-hidden /> : null}
                <Icon kind={PP_SUBNAV_ICONS[k]} size={17} stroke={1.8} style={{ color: on ? 'var(--ai-3)' : 'var(--ink-3)' }} />
                <span className="text-[14.5px]" style={{ fontWeight: on ? 600 : 500, color: on ? 'var(--ink-0)' : 'var(--ink-2)' }}>{k}</span>
              </a>
            );
          }
          return (
            <button
              key={k}
              type="button"
              onClick={() => onTabChange(k)}
              className="relative flex w-full cursor-pointer items-center gap-3 rounded-[var(--r-md)] border-0 px-3.5 py-[11px] text-left font-[var(--font-body)]"
              style={{
                background: on ? 'var(--cream-1)' : 'transparent',
                boxShadow: on ? 'var(--sh-1)' : 'none',
              }}
            >
              {on ? (
                <span
                  className="absolute bottom-[9px] left-0 top-[9px] w-[3px] rounded-full bg-[var(--ai-3)]"
                  aria-hidden
                />
              ) : null}
              <Icon
                kind={PP_SUBNAV_ICONS[k]}
                size={17}
                stroke={1.8}
                style={{ color: on ? 'var(--ai-3)' : 'var(--ink-3)' }}
              />
              <span
                className="text-[14.5px]"
                style={{
                  fontWeight: on ? 600 : 500,
                  color: on ? 'var(--ink-0)' : 'var(--ink-2)',
                }}
              >
                {k}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
